import type { FormEvent } from 'react'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { useAuth } from '@/context/AuthContext'
import AvatarUploader from '@/components/AvatarUploader'
import Card, { SectionLabel } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { Field, PasswordInput } from '@/components/ui/Field'
import TierBadge from '@/components/ui/TierBadge'
import { useToast } from '@/components/ui/toast'

/**
 * Customer account settings: profile photo, read-only details and password.
 * Detail edits stay with the shop (admin > Customers) - name and email feed
 * the sign-in and billing flows.
 */
export default function AccountSettings() {
  const { user, impersonating } = useAuth()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const change = useMutation({
    mutationFn: async () =>
      (
        await api.put('/profile/password', {
          current_password: current,
          password,
          password_confirmation: confirm,
        })
      ).data,
    onSuccess: (data) => {
      toast.success(data.message ?? 'Password updated')
      setCurrent('')
      setPassword('')
      setConfirm('')
    },
    onError: (err) => toast.error('Update failed', errorMessage(err)),
  })

  const errors = fieldErrors(change.error)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    change.mutate()
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        {/* An admin signed in as this customer may shop for them, but the
            account's own photo and password stay theirs; the server refuses
            both anyway. */}
        {!impersonating && (
          <Card>
            <SectionLabel>Profile photo</SectionLabel>
            <AvatarUploader />
          </Card>
        )}

        <Card>
          <SectionLabel>Account details</SectionLabel>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Name</dt>
              <dd className="min-w-0 break-all text-right font-semibold">{user?.name}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Email</dt>
              <dd className="min-w-0 break-all text-right font-semibold">{user?.email}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Phone</dt>
              <dd className="font-semibold">{user?.phone ?? '-'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Company</dt>
              <dd className="min-w-0 truncate text-right font-semibold">{user?.company_name ?? '-'}</dd>
            </div>
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted">Member tier</dt>
              <dd>
                {user?.tier ? <TierBadge name={user.tier.name} color={user.tier.color} icon={user.tier.icon} /> : '-'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Joined</dt>
              <dd className="font-semibold">{user?.created_at ? formatDate(user.created_at) : '-'}</dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-muted">
            Need a detail changed? Contact us - it affects your sign-in and billing.
          </p>
        </Card>
      </div>

      <Card className="self-start">
        <SectionLabel>Change password</SectionLabel>
        {impersonating ? (
          <p className="text-sm text-muted">Not available while signed in as {user?.name}.</p>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Current password" error={errors.current_password?.[0]}>
            <PasswordInput
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>
          <Field label="New password" error={errors.password?.[0]}>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
          <Field label="Confirm new password">
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
          <Button type="submit" loading={change.isPending}>
            <KeyRound className="size-4" /> Update password
          </Button>
          <p className="text-xs text-muted">Other signed-in devices are signed out when it changes.</p>
        </form>
        )}
      </Card>
    </div>
  )
}
